import React, { useState, useEffect } from 'react';
import Logo from '../components/common/Logo';
import Banner from '../components/common/Banner';
import { apiService } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';

const HomePage = () => {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        setLoading(true);
        const response = await apiService.getApprovalsAffiliations();
        // The API returns {success: true, data: {...}}, so we need response.data
        if (response && response.success && response.data) {
          setAboutData(response.data);
        } else {
          throw new Error('Invalid API response structure');
        }
      } catch (error) {
        setError('Failed to load about page data');
        console.error('Error fetching about data:', error);
        console.error('Error details:', error.message, error.response);
      } finally {
        setLoading(false);
      }
    };

    fetchAboutData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-crimson mb-4"></i>
          <p className="text-gray-600">Loading About Page...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-exclamation-circle text-4xl text-red-500 mb-4"></i>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full fade-in">
      <Banner 
        title={aboutData?.bannerTitle || "About FiT Engineering College"}
        subtitle={aboutData?.bannerSubtitle || "Excellence in Engineering Education | Building Tomorrow's Technology Leaders"}
        imageSrc={aboutData?.bannerImage ? (aboutData.bannerImage.startsWith('http') ? aboutData.bannerImage : getImageUrl(aboutData.bannerImage)) : "https://images.unsplash.com/photo-1562774053-701939374585?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"}
      />
      <div className="max-w-5xl mx-auto p-4 sm:p-6 md:p-10 pb-16">
        
        {/* Engineering College Overview */}
        <section className="mb-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="bg-white p-8 sm:p-12 md:p-16 rounded-2xl shadow-soft border border-border_gray flex flex-col items-center justify-center relative">
              <span className="absolute top-4 right-4 font-mono text-[10px] text-gray-400 border border-gray-200 px-2 py-1 rounded">FiT ENGINEERING COLLEGE</span>
              <Logo size="large" />
              <div className="text-center mt-4 sm:mt-6 md:mt-8">
                <p className="text-slate font-sans font-bold text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.3em] uppercase">Excellence in Technology Education</p>
              </div>
            </div>
            <div className="space-y-4 sm:space-y-6 mt-8 md:mt-0">
              <h2 className="text-2xl sm:text-3xl font-sans font-bold text-slate text-center md:text-left">
                {aboutData?.aboutTitle || "About FiT Engineering College"}
              </h2>
              <p className="text-sm sm:text-base text-gray-500 leading-relaxed">
                {aboutData?.aboutContent || "FiT Engineering College is a premier educational institution dedicated to providing world-class engineering and technology education. We are committed to fostering innovation, research excellence, and developing skilled professionals for the global technology landscape."}
              </p>
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                <i className="fas fa-info-circle mr-2"></i> 
                <strong>{aboutData?.aboutHighlight || "AKTU Affiliated & BTE UP Approved - Recognized for academic excellence."}</strong>
              </div>
            </div>
          </div>
        </section>


        {/* Approvals & Affiliations */}
        <section className="mb-12">
          <h2 className="text-xl sm:text-2xl font-sans font-bold text-slate mb-6 sm:mb-8 text-center md:text-left">Approvals & Affiliations</h2>
          
          {/* AICTE Section */}
          <div className="mb-8">
            <div className="bg-white p-4 sm:p-6 rounded-xl border border-border_gray">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-crimson rounded-full flex items-center justify-center mr-4">
                  <i className="fas fa-university text-white text-lg"></i>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate">
                    {aboutData?.aicteTitle || "AICTE"}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {aboutData?.aicteDescription || "All India Council for Technical Education"}
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold text-slate mb-2">Official Documents (Last 5 Years)</h4>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {aboutData?.aicteDocuments?.map((doc, index) => (
                    <a 
                      key={index}
                      href={doc.url}
                      target={doc.url === '#' ? '_self' : '_blank'}
                      rel={doc.url === '#' ? '' : 'noopener noreferrer'}
                      className="bg-surface p-3 rounded border border-gray-200 hover:shadow-sm transition block"
                    >
                      <i className="fas fa-file-pdf text-crimson mr-2"></i>
                      <span className="text-sm text-gray-700">{doc.title}</span>
                      <div className="text-xs text-gray-500 mt-1">
                        {doc.url === '#' ? 'Coming soon' : 'Click to download'}
                      </div>
                    </a>
                  )) || (
                    <div className="col-span-full text-center py-4 text-gray-500">
                      No documents available
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* AKTU Section */}
          <div className="mb-8">
            <div className="bg-white p-4 sm:p-6 rounded-xl border border-border_gray">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-crimson rounded-full flex items-center justify-center mr-4">
                  <i className="fas fa-graduation-cap text-white text-lg"></i>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate">
                    {aboutData?.aktuTitle || "AKTU"}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {aboutData?.aktuDescription || "Dr. A.P.J. Abdul Kalam Technical University"}
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold text-slate mb-2">Affiliation Documents (Last 5 Years)</h4>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {aboutData?.aktuDocuments?.map((doc, index) => (
                    <a 
                      key={index}
                      href={doc.url}
                      target={doc.url === '#' ? '_self' : '_blank'}
                      rel={doc.url === '#' ? '' : 'noopener noreferrer'}
                      className="bg-surface p-3 rounded border border-gray-200 hover:shadow-sm transition block"
                    >
                      <i className="fas fa-file-pdf text-crimson mr-2"></i>
                      <span className="text-sm text-gray-700">{doc.title}</span>
                      <div className="text-xs text-gray-500 mt-1">
                        {doc.url === '#' ? 'Coming soon' : 'Click to download'}
                      </div>
                    </a>
                  )) || (
                    <div className="col-span-full text-center py-4 text-gray-500">
                      No documents available
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* BTE UP Section */}
          <div className="mb-8">
            <div className="bg-white p-4 sm:p-6 rounded-xl border border-border_gray">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-crimson rounded-full flex items-center justify-center mr-4">
                  <i className="fas fa-tools text-white text-lg"></i>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate">
                    {aboutData?.bteTitle || "BTE UP"}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {aboutData?.bteDescription || "Board of Technical Education, Uttar Pradesh"}
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold text-slate mb-2">Approval Documents (Last 5 Years)</h4>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {aboutData?.bteDocuments?.map((doc, index) => (
                    <a 
                      key={index}
                      href={doc.url}
                      target={doc.url === '#' ? '_self' : '_blank'}
                      rel={doc.url === '#' ? '' : 'noopener noreferrer'}
                      className="bg-surface p-3 rounded border border-gray-200 hover:shadow-sm transition block"
                    >
                      <i className="fas fa-file-pdf text-crimson mr-2"></i>
                      <span className="text-sm text-gray-700">{doc.title}</span>
                      <div className="text-xs text-gray-500 mt-1">
                        {doc.url === '#' ? 'Coming soon' : 'Click to download'}
                      </div>
                    </a>
                  )) || (
                    <div className="col-span-full text-center py-4 text-gray-500">
                      No documents available
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Accreditations */}
        <section className="mb-12">
          <h2 className="text-xl sm:text-2xl font-sans font-bold text-slate mb-6 sm:mb-8 text-center md:text-left">Accreditations</h2>
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            {aboutData?.accreditations?.map((accreditation, index) => (
              <div 
                key={index}
                className="bg-white p-4 sm:p-6 md:p-8 rounded-xl border border-border_gray"
              >
                <div className="flex items-center gap-3 sm:gap-4 mb-4">
                  <div className="w-12 h-12 bg-gold rounded-full flex items-center justify-center">
                    <i className={`${accreditation.icon || 'fas fa-medal'} text-white`}></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-slate">{accreditation.name}</h4>
                    <p className="text-sm text-gray-500">{accreditation.fullName}</p>
                  </div>
                </div>
                
                {/* Document Attachment */}
                {accreditation.documentUrl && accreditation.documentUrl !== '#' && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <a 
                      href={accreditation.documentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-crimson hover:text-crimson/80 font-medium"
                    >
                      <i className="fas fa-file-pdf"></i>
                      View Certificate
                      <i className="fas fa-external-link-alt text-xs"></i>
                    </a>
                  </div>
                )}
              </div>
            )) || (
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                <div className="bg-white p-4 sm:p-6 md:p-8 rounded-xl border border-border_gray flex items-center gap-3 sm:gap-4">
                  <div className="w-12 h-12 bg-gold rounded-full flex items-center justify-center">
                    <i className="fas fa-medal text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-slate">NAAC</h4>
                    <p className="text-sm text-gray-500">National Assessment and Accreditation Council</p>
                  </div>
                </div>
                <div className="bg-white p-4 sm:p-6 md:p-8 rounded-xl border border-border_gray flex items-center gap-3 sm:gap-4">
                  <div className="w-12 h-12 bg-gold rounded-full flex items-center justify-center">
                    <i className="fas fa-award text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-slate">NBA</h4>
                    <p className="text-sm text-gray-500">National Board of Accreditation</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

      </div>
    </div>
  );
};

export default HomePage;